<template>
  <parent>
    <div
      class="origins-form-wrapper h-100"
    >
      <create-healthcheck
        v-if="healthcheckForm"
        :distribution-id="distributionId"
        @added="healthcheckAdded"
        @closed="healthcheckForm = false"
      />
      <template v-else>
        <loading-page v-if="loading" />
        <form-wrapper v-else>
          <template v-slot:body>
            <validation-observer
              ref="originRules"
              tag="form"
            >
              <paper-simple v-if="!noOriginBuckets || externalAccounts.length > 0">
                <custom-input-radio
                  v-model="origin.originTypeId"
                  label="Origin Type"
                  :options="getOriginTypeOptions()"
                  @change="onChangeOriginId"
                />
              </paper-simple>
              <paper-simple>
                <div
                  v-if="custom"
                  class="d-flex align-items-center"
                >
                  <custom-select
                    v-model="origin.originProtocolPolicy"
                    style="max-width:100px !important; margin-top: 7px;"
                    :options="getObjectOptions(originProtocolPolicyOptions)"
                  />
                  <validation-provider
                    #default="{ errors }"
                    class="top-fade-in w-100"
                    name="Origin Host"
                    rules="required|ip"
                  >
                    <input-field
                      label="Origin Host"
                      label-position="top"
                      class="custom-label-position"
                      :info="messages.originDomainName"
                      :error="errors.length ? errors[0] : null"
                      @openInfo="showInfo(messages.originDomainName.title, messages.originDomainName.message)"
                    >
                      <b-form-input
                        v-model="origin.domainName"
                        placeholder="mydomain.com or 1.1.1.1"
                      />
                    </input-field>
                  </validation-provider>

                </div>

                <validation-provider
                  v-else
                  #default="{ errors }"
                  class="top-fade-in"
                  name="Origin Bucket"
                  rules="required"
                >
                  <input-field
                    label="Origin Bucket"
                    class="mr-35"
                    label-position="top"
                    :error="errors.length ? errors[0] : null"
                  >
                    <div class="col-12 p-0 d-flex flex-wrap">
                      <b-dropdown
                        id="object-options"
                        :disabled="fetchingExternalAccounts"
                        variant="bg-white"
                        no-caret
                        class="col-md-6 col-sm-12 mb-sm-2 m-md-0 p-0"
                        :state="errors.length > 0 ? false : null"
                      >
                        <template
                          #button-content
                        >
                          <div @click="toggleOpenExternalAccount">
                            <img
                              :src="selectedAccount.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : selectedAccount.providerImageUrl"
                              width="30px"
                              height="30px"
                              alt="Storage External Account"
                              class="mr-1"
                            >
                            <span><b>{{ selectedAccount.parentId === '0' ? 'Captain' : selectedAccount.username }}</b></span>
                            <GjIcon
                              class="ml-1"
                              :class="isOpenExternalAccount ? 'arrow' : 'arrow-down'"
                              name="ArrowDown"
                            />
                          </div>
                        </template>
                        <ul
                          v-for="(account, property) in externalAccounts"
                          :key="property"
                          class="list-unstyled m-0 p-0"
                        >
                          <b-dropdown-item @click="changeSelectedAccount(account)">
                            <img
                              :src="account.parentId === '0' ? require('@/assets/images/logo/Captain.svg') : account.providerImageUrl"
                              width="30px"
                              height="30px"
                              alt=""
                              class="mr-1"
                            >
                            <span class="main"><strong>{{ account.parentId === '0' ? 'Captain ' : account.username }}</strong></span>
                          </b-dropdown-item>

                        </ul>
                      </b-dropdown>
                      <custom-select
                        id="origin-bucket"
                        v-model="selectedBucket"
                        class="col-md-6 col-sm-12 p-0 bucket-input"
                        label="name"
                        :loading="fetchingBuckets || fetchingExternalAccounts"
                        :disabled="isBucketDisabled() || fetchingBuckets"
                        :state="errors.length > 0 ? false:null"
                        :options="getBucketOptions(accountBuckets)"
                        @change="onChangeOriginId"
                      />
                    </div>
                  </input-field>
                </validation-provider>

                <input-field
                  label="Origin Path"
                  label-position="top"
                  :info="messages.originPath"
                  @openInfo="showInfo(messages.originPath.title, messages.originPath.message)"
                >
                  <b-form-input
                    v-model="origin.path"
                    placeholder="/"
                  />
                </input-field>
                <validation-provider
                  #default="{ errors }"
                  name="Origin Id"
                  rules="required|max:50"
                >
                  <input-field
                    label="Origin ID"
                    label-position="top"
                    :info="messages.originId"
                    :error="errors.length ? errors[0] : null"
                    @openInfo="showInfo(messages.originId.title, messages.originId.message)"
                  >
                    <b-form-input
                      v-model="origin.originId"
                      :state="errors.length > 0 ? false:null"
                      @change="originIdChanged = true"
                    />
                  </input-field>
                </validation-provider>
              </paper-simple>

              <paper-simple>
                <div class="d-flex align-items-center mb-1">
                  <span class="mr-1">Enable Healthchecks</span>
                  <b-form-checkbox
                    v-model="hasHealthcheck"
                    class="mb-1"
                    switch
                  />
                </div>
                <div
                  v-if="hasHealthcheck"
                  class="top-fade-in position-relative"
                >
                  <div
                    v-if="fetchingHealthchecks"
                    class="healthcheck-spinner"
                  >
                    <b-spinner variant="primary" />
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="Healthcheck"
                    :rules="hasHealthcheck ? 'required' : ''"
                  >
                    <input-field
                      :class="{'opacity-7': !hasHealthcheck}"
                      class="mr-35"
                      label="Healthcheck"
                      label-position="top"
                      :error="errors.length ? errors[0] : null"
                    >
                      <custom-select
                        v-model="origin.healthcheckId"
                        :disabled="!hasHealthcheck"
                        :state="errors.length > 0 ? false:null"
                        :options="healthchecks"
                      />
                    </input-field>
                  </validation-provider>
                  <b-button
                    variant="bg-white"
                    class="mb-2"
                    @click="healthcheckForm = true"
                  >
                    <GjIcon
                      class="icon-left"
                      name="Plus"
                    />
                    Create a new healthcheck
                  </b-button>
                </div>
              </paper-simple>

              <template v-if="custom">
                <paper-simple class="top-fade-in">

                  <formInput
                    title="Origin Connection Attempts"
                    @info="showInfo(messages.originConnectionAttempts.title, messages.originConnectionAttempts.message)"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.connectionAttempts"
                      />
                    </template>
                  </formInput>

                  <formInput
                    title="Origin Connection Timeout"
                    @info="showInfo(messages.originConnectionTimeout.title, messages.originConnectionTimeout.message)"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.connectionTimeout"
                      />
                    </template>
                  </formInput>

                  <formInput
                    title="Origin Response Timeout"
                    @info="showInfo(messages.originResponseTimeout.title, messages.originResponseTimeout.message)"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.originResponseTimeout"
                      />
                    </template>
                  </formInput>

                  <formInput
                    title="Origin Keep-alive Timeout"
                    @info="showInfo(messages.originKeepaliveTimeout.title, messages.originKeepaliveTimeout.message)"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.originKeepAliveTimeout"
                      />
                    </template>
                  </formInput>

                  <formInput
                    no-icon
                    title="HTTP Port"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.httpPort"
                        :disabled="origin.originProtocolPolicy === 1"
                      />
                      <custom-alert icon-name="Info">
                        {{ messages.httpPort.message }}
                      </custom-alert>
                    </template>
                  </formInput>

                  <formInput
                    title="HTTPS Port"
                    @info="showInfo(messages.httpsPort.title, messages.httpsPort.message)"
                  >
                    <template v-slot:input>
                      <b-form-input
                        v-model="origin.httpsPort"
                        :disabled="origin.originProtocolPolicy === 0"
                      />
                    </template>
                  </formInput>

                  <formInput
                    title="Origin Custom Headers"
                    no-icon
                  >
                    <template v-slot:input>
                      <tags
                        :form-input="true"
                        :tags="originHeaders"
                        tag-name="header"
                        @remove-field="onRemoveField"
                      />
                    </template>
                  </formInput>
                </paper-simple>
              </template>
              <div v-else>
                <paper-simple>
                  <custom-input-radio
                    v-model="origin.bucketAccessId"
                    label="Bucket Access"
                    :info="messages.bucketAccess"
                    :options="getObjectOptions(origin.bucketAccessOptions)"
                    @openInfo="showInfo(messages.bucketAccess.title, messages.bucketAccess.message)"
                  />
                </paper-simple>
              </div>

            </validation-observer>
          </template>

          <template v-slot:footer>
            <b-button
              variant="bg-white"
              @click="$emit('close')"
            >
              Cancel
            </b-button>
            <button-loading
              variant="bg-primary"
              class="ml-16"
              :spinning="details ? updatingOrigin : addingOrigin"
              :disabled="fetchingBuckets"
              @click="validationOriginSettings"
            >
              {{ details ? 'Update' : 'Create' }}
            </button-loading>
          </template>

        </form-wrapper>
      </template>

      <info
        v-if="openInfo"
        :title="infoTitle"
        :message="infoMessage"
        @close="openInfo = false"
      />
    </div>
  </parent>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import CreateHealthcheck from './CreateHealthcheck.vue'
import Tags from '@/components/tags/Tags.vue'
import redirectError from '@/services/redirectError'
import { required, ip, max } from '@validations'
import messages from '../../../messages'

export default {
  name: 'DistributionOriginsForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    CustomInputRadio,
    Tags,
    CreateHealthcheck,
  },
  props: {
    details: {
      type: Boolean,
      default: false,
    },
    originId: {
      type: String,
      default: null,
    },
    distributionId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      required,
      ip,
      max,
      messages,
      openInfo: false,
      infoTitle: '',
      infoMessage: '',
      selected: null,
      originHeaders: [],
      originIdChanged: false,
      noOriginBuckets: false,
      hasHealthcheck: false,
      healthcheck: null,
      healthcheckForm: false,
      originProtocolPolicyOptions: {
        'http://': 0,
        'https://': 1,
      },
      selectedBucket: '',
      isOpenExternalAccount: false,
    }
  },
  computed: {
    lastIndex() {
      return this.originHeaders.length ? this.originHeaders[this.originHeaders.length - 1].index : 0
    },
    firstIndex() {
      return this.originHeaders[0].index
    },
    origin() {
      return this.$store.state.cdn.origin
    },
    loading() {
      return this.$store.state.cdn.gettingOrigin
    },
    updatingOrigin() {
      return this.$store.state.cdn.updatingOrigin
    },
    addingOrigin() {
      return this.$store.state.cdn.addingOrigin
    },
    custom() {
      return this.origin.originTypeId === 0
    },
    originBucketId() {
      const foundOriginBucket = this.origin.bucketListModel.find(b => b.name === this.origin.bucket)
      return foundOriginBucket?.name ? foundOriginBucket.name : this.accountBuckets[0]?.name
    },
    healthchecks() {
      return this.$store.state.cdn.healthchecks.length ? [{ value: null, text: 'Select' }, ...this.$store.state.cdn.healthchecks.map(el => ({ value: el.id, text: el.name }))] : [{ value: null, text: 'Select' }]
    },
    fetchingHealthchecks() {
      return this.$store.state.cdn.fetchingHealthchecks
    },
    fetchingExternalAccounts() {
      return this.$store.state.externalAccount.fetchingExternalAccounts
    },
    externalAccounts() {
      return this.$store.state.externalAccount.externalAccounts
    },
    fetchingBuckets() {
      return this.$store.state.accountBuckets.fetchingBuckets
    },
    accountBuckets: {
      get() {
        return this.$store.state.accountBuckets.buckets
      },
      set(account) {
        const arr = [{ ...account }]
        this.$store.commit('accountBuckets/UPDATE_BUCKETS', arr)
      },
    },
    selectedAccount: {
      get() {
        return this.$store.state.externalAccount.externalAccount || null
      },
      set(account) {
        this.$store.commit('externalAccount/CHANGE_ACCOUNT', account)
      },
    },
    accountId: {
      get() {
        return this.$store.state.externalAccount.accountId
      },
      set(val) {
        this.$store.commit('externalAccount/SET_ACCOUNT_ID', val)
      },
    },
  },
  beforeDestroy() {
    this.accountId = 0
  },
  created() {
    this.getOrigin()
    this.fetchHealthchecks()
  },
  methods: {
    onRemoveField(index) {
      this.originHeaders = this.originHeaders.filter(obj => obj.index !== index)
    },
    onSave() {
      let isNull = true
      this.origin.customHeaders = []
      this.originHeaders.forEach(element => {
        if (element.key !== '' && element.value !== '') {
          isNull = false
          this.origin.customHeaders.push({
            name: element.key,
            value: element.value,
          })
        }
      })
      if (isNull) {
        this.origin.customHeaders = null
      }

      const localOrigin = {
        distributionId: this.origin.distributionId,
        id: this.origin.id,
        domainName: this.origin.domainName,
        path: this.origin.path,
        originId: this.origin.originId,
        originTypeId: this.origin.originTypeId,
        minimumOriginSslProtocol: this.origin.minimumOriginSslProtocol,
        originProtocolPolicy: this.origin.originProtocolPolicy,
        httpPort: this.origin.httpPort,
        httpsPort: this.origin.httpsPort,
        originResponseTimeout: this.origin.originResponseTimeout,
        originKeepAliveTimeout: this.origin.originKeepAliveTimeout,
        connectionAttempts: this.origin.connectionAttempts,
        connectionTimeout: this.origin.connectionTimeout,
        followRedirects: this.origin.followRedirects,
        bucket: !this.custom ? this.selectedBucket : null,
        bucketAccessId: this.origin.bucketAccessId,
        customHeaders: this.origin.customHeaders,
        healthcheckId: this.hasHealthcheck ? this.origin.healthcheckId : null,
        storageAccountId: !this.custom ? this.selectedAccount.id : null,
      }
      this.$emit('submit', localOrigin)
    },
    getOriginTypeOptions() {
      return this.getObjectOptions(this.origin.originTypeOptions).sort((a, b) => {
        if (a.value < b.value) { return 1 }
        if (a.value > b.value) { return -1 }
        return 0
      })
    },
    toggleOpenExternalAccount() {
      this.isOpenExternalAccount = !this.isOpenExternalAccount
    },
    validationOriginSettings() {
      return new Promise((resolve, reject) => {
        this.$refs.originRules.validate().then(success => {
          if (success) {
            this.onSave()
          } else {
            reject()
          }
        })
      })
    },
    onChangeOriginId() {
      if (!this.originIdChanged) {
        if (this.custom) {
          const id = this.origin.domainName
          this.origin.originId = id !== null ? `custom-${id.substring(0, 43)}` : ''
          this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
        } else {
          this.origin.originId = this.originBucketId
        }
      }
    },
    getOrigin() {
      this.$store.dispatch('cdn/getOrigin', {
        distributionId: this.details ? this.distributionId : this.$route.params.id,
        originId: this.details ? this.originId : 'model',
      })
        .then(() => {
          if (this.origin.originId !== null) {
            this.originIdChanged = true
          }
          if (this.origin.bucketListModel.length < 1 && this.origin.storageAccountId === null) {
            this.noOriginBuckets = true
            this.origin.originTypeId = 0
          }
          if (!this.noOriginBuckets && this.origin.bucket === null) {
            this.origin.bucket = this.origin.bucketListModel[0]?.name
            this.selectedBucket = this.origin.bucketListModel[0]?.name
            this.onChangeOriginId()
          }
          if (this.details) {
            if (this.origin.healthcheckId) {
              this.hasHealthcheck = true
            }
            this.selectedBucket = this.origin.bucket
            this.origin.customHeaders.forEach(element => {
              this.originHeaders.push(
                {
                  index: this.lastIndex + 1,
                  id: element.id,
                  key: element.name,
                  value: element.value,
                },
              )
            })
          } else {
            this.origin.distributionId = this.$route.params.id
          }
        })
        .catch(err => redirectError(err, this.$router, this.$route.path))
        .finally(() => {
          this.fetchExternalAccounts()
        })
    },
    fetchExternalAccounts() {
      this.$store
        .dispatch('externalAccount/fetchAccounts')
        .then(() => {
          let account = null
          if (this.details && this.origin.storageAccountId != null) {
            account = this.externalAccounts.find(a => a.id === this.origin.storageAccountId)
          } else {
            account = this.externalAccounts.find(a => a.id === this.selectedAccount?.id)
          }

          this.changeSelectedAccount(account)
        })
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors
              || 'Something went wrong while fetching all external accounts.',
          )
        })
        .finally(() => {})
    },
    getBuckets() {
      this.$store
        .dispatch('accountBuckets/fetchBuckets', this.accountId)
        .then(() => {
          this.changeSelectedBucket()
          this.formInitialized = true
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 424) {
            this.$store.commit('accountBuckets/SET_SHOW_INTRO', true)
            this.$store.commit('buckets/SET_SHOW_INTRO', true)
            return
          }
          if (status === 426 || status === 428) {
            this.$store.commit('accountBuckets/SET_SERVICE_UNAVAILABLE', status === 428)
            this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
            return
          }
          if (status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching buckets.',
          )
          // eslint-disable-next-line eqeqeq
          if (this.selectedAccount.parentId == 0) {
            this.$store.dispatch('accountBuckets/clearBucket')
          }
        })
    },
    changeSelectedBucket() {
      if (this.details) {
        if (this.origin.bucket) {
          const foundBucket = this.accountBuckets.find(x => x.name === this.origin.bucket)
          this.selectedBucket = foundBucket?.name
        } else {
          this.selectedBucket = this.selectedAccount?.bucketName
        }
      } else {
        this.selectedBucket = this.accountBuckets[0]?.name
      }
    },
    changeSelectedAccount(account) {
      this.selectedAccount = account
      this.accountId = account?.id || 0
      const bucketName = this.selectedAccount?.bucketName

      // eslint-disable-next-line eqeqeq
      if (this.selectedAccount !== null && (this.selectedAccount?.parentId == 0 || bucketName === null)) {
        this.getBuckets()
      } else {
        this.accountBuckets = { name: bucketName }
        this.selectedBucket = bucketName
        this.$store.commit('accountBuckets/CANCEL_FETCHING_BUCKETS')
      }

      return this.selectedAccount
    },
    isBucketDisabled() {
      // eslint-disable-next-line eqeqeq
      return this.selectedAccount?.bucketName !== null && this.selectedAccount?.parentId != 0
    },
    showInfo(title, info) {
      this.openInfo = true
      this.infoTitle = title
      this.infoMessage = info
    },
    getBucketOptions: options => options.map(o => ({ text: o.name, value: o.name })),
    fetchHealthchecks() {
      this.$store.dispatch('cdn/fetchHealthchecks', this.distributionId)
    },
    healthcheckAdded() {
      this.fetchHealthchecks()
      this.healthcheckForm = false
    },
  },
}
</script>
<style lang="scss" scoped>

.custom-label-position {
  ::v-deep {
    .labelMargin {
      position: relative;
      left: -98px;
    }
  }
}

.origins-form-wrapper {
  ::v-deep {
    .form-wrapper {
      &__body {
        > div {
          margin: auto;
        }
      }
    }
    .card.paper {
      .card-body {
        padding-bottom: 0;
      }
    }

    .info-assistant {
      margin-left: auto;
    }
  }
}

.bucket-input::v-deep{
  .dropdown-menu {
    height: auto;
    max-height: 200px;
    overflow-y: auto;
  }
}

.mr-35 {
  margin-right: 35px;
}

.healthcheck-spinner {
  z-index: 1;
  position: absolute;
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  z-index: 2;
}
</style>
